import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../context";
import {
  initFullStory,
  readStoreLeadMobileForFullStory,
  setFullStoryIdentity,
} from "../common/service";
import { initGA, initMetaPixel } from "../common/analytics";
import { getCurrentUser, setAuthenticatedUser } from "../auth/service";
import StoreLayout from "./StoreLayout";
import { isStore, setPageMeta } from "../utils";
import storeApi from "./storeApi";
import { trackStoreLeadSignupStart } from "./leadActivity";
import { leadMobileFromUrl } from "./leadMobileFromUrl";
import { clearPendingSampleCabinetIntent } from "./service";
import {
  clearStoreProjectsFetchCache,
  fetchStoreProjectsListForUser,
  shouldBootstrapStoreProjectsList,
} from "./storeProjectsBootstrap";

const LEAD_SOURCE_QUERY_KEYS = [
  "lead_source",
  "source",
  "src",
  "ref",
  "referral_source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const captureLeadSourceFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const leadMobile = leadMobileFromUrl(url);
    if (leadMobile) {
      localStorage.setItem("storeLeadMobile", leadMobile);
    }

    const existing = localStorage.getItem("storeLeadSource");
    if (existing) return;
    const source =
      url.searchParams.get("lead_source") ||
      url.searchParams.get("utm_source") ||
      url.searchParams.get("source") ||
      url.searchParams.get("src") ||
      url.searchParams.get("ref") ||
      url.searchParams.get("referral_source");
    if (!source) return;
    const meta = {};
    LEAD_SOURCE_QUERY_KEYS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) meta[key] = value;
    });
    localStorage.setItem("storeLeadSource", source);
    localStorage.setItem("storeLeadSourceUrl", url.href);
    localStorage.setItem("storeLeadSourceMeta", JSON.stringify(meta));
  } catch (_err) {
    // Non-fatal; lead source capture should not block app bootstrap.
  }
};

const STORE_DEALER_REFERRAL_KEY = "storeDealerReferral";

function hasStoreAuthToken() {
  return Boolean(
    localStorage.getItem("storeAccessToken") ||
      localStorage.getItem("accessToken")
  );
}

function isPersistedAuthenticatedStoreUser(user) {
  if (!user || typeof user !== "object") return false;
  if (user.temp === true) return false;
  const mobile = String(user.mobile ?? "")
    .trim()
    .toLowerCase();
  if (!mobile || mobile.startsWith("guest_")) return false;
  return true;
}

/**
 * Logged-in buyers with token + cached user: skip GET /store/session on each navigation.
 * Session still runs after login/OTP and on lead-only visits (see shouldFetchStoreSessionOnPageLoad).
 */
function shouldSkipStoreSessionOnPageLoad(cachedUser) {
  return (
    hasStoreAuthToken() && isPersistedAuthenticatedStoreUser(cachedUser)
  );
}

/** Anonymous lead / referral attribution — needs session once per tab for store_lead_context. */
function anonymousStoreSessionBootstrapNeeded() {
  if (hasStoreAuthToken()) return false;
  return Boolean(
    localStorage.getItem("storeReferralCode") ||
      localStorage.getItem("storeLeadMobile") ||
      localStorage.getItem("storeLeadSource")
  );
}

function shouldFetchStoreSessionOnPageLoad(cachedUser) {
  if (shouldSkipStoreSessionOnPageLoad(cachedUser)) return false;
  if (anonymousStoreSessionBootstrapNeeded()) return true;
  return hasStoreAuthToken();
}

function readCachedDealerReferral() {
  try {
    const raw = localStorage.getItem(STORE_DEALER_REFERRAL_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function persistCachedDealerReferral(referral) {
  if (referral && typeof referral === "object") {
    localStorage.setItem(STORE_DEALER_REFERRAL_KEY, JSON.stringify(referral));
  } else {
    localStorage.removeItem(STORE_DEALER_REFERRAL_KEY);
  }
}

function parseStoreSessionResponse(sessionResponse) {
  const storeUser =
    sessionResponse?.user ||
    sessionResponse?.data?.user ||
    sessionResponse?.data?.data?.user ||
    sessionResponse?.data?.data ||
    sessionResponse?.data ||
    null;
  const dealerReferral =
    sessionResponse?.dealer_referral ||
    sessionResponse?.data?.dealer_referral ||
    null;
  return { storeUser, dealerReferral };
}

function readCachedStoreUserFromLocalStorage() {
  const hasAuthToken =
    localStorage.getItem("storeAccessToken") ||
    localStorage.getItem("accessToken");
  if (!hasAuthToken) return null;
  try {
    return getCurrentUser();
  } catch (_err) {
    return null;
  }
}

const StoreShellApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [catalogs, setCatalogs] = useState({});
  const [projectInfo, setProjectInfo] = useState({});
  const [clearSearch, setClearSearch] = useState(false);
  const [isSubModule, setIsSubModule] = useState(false);
  const [isRemarksOn, setIsRemarksOn] = useState(false);
  const [isExpoOn, setIsExpoOn] = useState(false);
  const [bomSummary, setBomSummary] = useState({ loaded: false });
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [forceRefreshSpaceConfigStatus, setForceRefreshSpaceConfigStatus] =
    useState(false);
  const [orderContext, setOrderContext] = useState([]);
  const [currentUser, setCurrentUser] = useState(() =>
    readCachedStoreUserFromLocalStorage()
  );
  const [storeProjectsList, setStoreProjectsList] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartUuid, setCartUuid] = useState(
    () => localStorage.getItem("store_cart_uuid") || null
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [storeFilters, setStoreFilters] = useState({});
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [dealerReferral, setDealerReferral] = useState(() =>
    readCachedDealerReferral()
  );
  const authModalReturnToRef = useRef(null);
  const leadOnlySessionBootstrappedRef = useRef(false);

  const popAuthModalReturnTarget = useCallback(() => {
    const v = authModalReturnToRef.current;
    authModalReturnToRef.current = null;
    return v;
  }, []);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((v) => !v);
  const openAuthModal = useCallback((options) => {
    const path =
      typeof options?.returnTo?.pathname === "string"
        ? options.returnTo.pathname.trim()
        : "";
    authModalReturnToRef.current =
      path.length > 0
        ? {
            pathname: path,
            ...(options.returnTo.state !== undefined
              ? { state: options.returnTo.state }
              : {}),
          }
        : null;
    trackStoreLeadSignupStart();
    setIsAuthModalOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => {
    authModalReturnToRef.current = null;
    setIsAuthModalOpen(false);

    // Use localStorage user, not context state — onClose runs right after login before
    // setCurrentUser has re-rendered, and would wrongly clear the sample-cabinet intent.
    const resolvedUser = getCurrentUser();
    const mobile = String(resolvedUser?.mobile ?? "").trim();
    const isAuthenticated =
      mobile.length > 0 && !mobile.toLowerCase().startsWith("guest_");
    if (!isAuthenticated) {
      clearPendingSampleCabinetIntent();
    }
  }, []);
  const openProfileModal = useCallback(() => setIsProfileModalOpen(true), []);
  const closeProfileModal = useCallback(() => setIsProfileModalOpen(false), []);

  const bootstrapStoreSession = useCallback(async () => {
    const leadOnly =
      !localStorage.getItem("accessToken") &&
      !localStorage.getItem("storeAccessToken") &&
      !localStorage.getItem("storeReferralCode");
    if (leadOnly && leadOnlySessionBootstrappedRef.current) {
      return { storeUser: null, dealerReferral: null };
    }

    try {
      const sessionResponse = await storeApi.sessionAPI.getCurrentSession();
      if (leadOnly) leadOnlySessionBootstrappedRef.current = true;
      return parseStoreSessionResponse(sessionResponse);
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("storeAccessToken");
      }
      return { storeUser: null, dealerReferral: null };
    }
  }, []);

  const refreshStoreProjects = useCallback(async () => {
    if (!shouldBootstrapStoreProjectsList(currentUser)) {
      setStoreProjectsList([]);
      return [];
    }
    try {
      const list = await fetchStoreProjectsListForUser(currentUser);
      setStoreProjectsList(list);
      return list;
    } catch (_err) {
      return [];
    }
  }, [currentUser]);

  useEffect(() => {
    if (!shouldBootstrapStoreProjectsList(currentUser)) {
      setStoreProjectsList([]);
      return undefined;
    }
    let cancelled = false;
    void fetchStoreProjectsListForUser(currentUser).then((list) => {
      if (!cancelled) setStoreProjectsList(list);
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, currentUser?.dealer, currentUser?.admin]);

  const refreshStoreSession = useCallback(async () => {
    const { storeUser, dealerReferral: ref } = await bootstrapStoreSession();
    if (storeUser) {
      setCurrentUser(storeUser);
      const token =
        localStorage.getItem("storeAccessToken") ||
        localStorage.getItem("accessToken");
      if (token) setAuthenticatedUser({ token, user: storeUser });
    }
    if (ref) setDealerReferral(ref);
    return { storeUser, dealerReferral: ref };
  }, [bootstrapStoreSession]);

  useEffect(() => {
    persistCachedDealerReferral(dealerReferral);
  }, [dealerReferral]);

  useEffect(() => {
    const initialize = async () => {
      captureLeadSourceFromUrl();

      const fullStoryInitPromise = initFullStory();
      const cachedUser = readCachedStoreUserFromLocalStorage();
      if (cachedUser) setCurrentUser(cachedUser);

      let storeUser = null;
      let sessionDealerReferral = null;
      if (shouldFetchStoreSessionOnPageLoad(cachedUser)) {
        const sessionResult = await bootstrapStoreSession();
        storeUser = sessionResult.storeUser;
        sessionDealerReferral = sessionResult.dealerReferral;
      }

      if (storeUser) {
        setCurrentUser(storeUser);
        const token =
          localStorage.getItem("storeAccessToken") ||
          localStorage.getItem("accessToken");
        if (token) setAuthenticatedUser({ token, user: storeUser });
      } else if (
        shouldFetchStoreSessionOnPageLoad(cachedUser) &&
        !cachedUser
      ) {
        setCurrentUser(null);
      }

      if (sessionDealerReferral) setDealerReferral(sessionDealerReferral);

      await fullStoryInitPromise;
      const resolvedAuthUser =
        storeUser || cachedUser || getCurrentUser() || null;
      const leadMobileForFullStory = readStoreLeadMobileForFullStory();
      setFullStoryIdentity(resolvedAuthUser, {
        leadMobile: leadMobileForFullStory,
      });

      initGA();
      initMetaPixel();
    };

    initialize();
  }, []);

  useEffect(() => {
    const onAuthRequired = () => {
      // Avoid repeated modal opens during cascaded 401s.
      setIsAuthModalOpen((alreadyOpen) => {
        if (alreadyOpen) return alreadyOpen;
        return true;
      });
      // Preserve current route so login can return here.
      openAuthModal({
        returnTo: {
          pathname: location.pathname,
          ...(location.state !== undefined ? { state: location.state } : {}),
        },
      });
    };
    window.addEventListener("store:auth-required", onAuthRequired);
    return () =>
      window.removeEventListener("store:auth-required", onAuthRequired);
  }, [location.pathname, location.state, openAuthModal]);

  useEffect(() => {
    const leadMobile = readStoreLeadMobileForFullStory();
    if (!currentUser?.id && !leadMobile) return;
    setFullStoryIdentity(currentUser, { leadMobile });
  }, [
    currentUser?.id,
    currentUser?.name,
    currentUser?.mobile,
    currentUser?.temp,
    currentUser?.admin,
  ]);

  useEffect(() => {
    setPageMeta();
    if (location.pathname === "/store" && !isStore()) {
      navigate("/store");
    }
  }, [location.pathname, navigate]);

  return (
    <AppContext.Provider
      value={{
        projectInfo,
        setProjectInfo,
        bomSummary,
        setBomSummary,
        catalogs,
        setCatalogs,
        catalogsLoading,
        setCatalogsLoading,
        forceRefreshSpaceConfigStatus,
        setForceRefreshSpaceConfigStatus,
        clearSearch,
        setClearSearch,
        isSubModule,
        setIsSubModule,
        isRemarksOn,
        setIsRemarksOn,
        isExpoOn,
        setIsExpoOn,
        orderContext,
        setOrderContext,
        currentUser,
        setCurrentUser,
        cart,
        setCart,
        cartUuid,
        setCartUuid,
        isCartOpen,
        setIsCartOpen,
        openCart,
        closeCart,
        toggleCart,
        storeFilters,
        setStoreFilters,
        isAuthModalOpen,
        setIsAuthModalOpen,
        openAuthModal,
        closeAuthModal,
        popAuthModalReturnTarget,
        isProfileModalOpen,
        openProfileModal,
        closeProfileModal,
        getCurrentUser,
        dealerReferral,
        setDealerReferral,
        storeProjectsList,
        setStoreProjectsList,
        refreshStoreProjects,
        clearStoreProjectsCache: clearStoreProjectsFetchCache,
        refreshStoreSession,
      }}
    >
      <div className="StoreApp">
        <StoreLayout>
          <Outlet />
        </StoreLayout>
      </div>
    </AppContext.Provider>
  );
};

export default StoreShellApp;
